import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Menu, MenuItem, Button, Box, Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Iconify from '../../../../../reusable-components/iconify';
import { getOrderingPunchoutVendorsData } from '../../../../../api/order-sheet';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { VendorOrderLinkList } from './VendorOrderLinkList';

export default function VendorOrder() {
  const { enqueueSnackbar } = useSnackbar();
  const { facilityId = '' } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const categoryId = urlParams.get('categoryId');

  const [links, setLinks] = useState(null);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState(null);
  const [orderByCategoryAllowedVendors, setOrderByCategoryAllowedVendors] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [noVendors, setNoVendors] = useState(false);
  const [confirmPunchout, setConfirmPunchout] = useState(null);
  const [loadingVendorPunchout, setLoadingVendorPunchout] = useState(null);

  useEffect(() => {
    if (anchorEl !== null) fetchData();
  }, [anchorEl]);

  const fetchData = () => getOrderingPunchoutVendorsData(categoryId, facilityId)
    .then((res) => {
      setLinks(res.data?.links?.slice().sort((a, b) => b.hasAccount - a.hasAccount || a.vendorName.localeCompare(b.vendorName)));
      setCategories(res.data.categories ?? []);
      setCategory(res.data?.categories?.length ? res.data.categories[0] : null);
      setOrderByCategoryAllowedVendors(res.data?.orderByCategoryAllowedVendors);
      setNoVendors(!res.data?.links?.length);
    })
    .catch(() => enqueueSnackbar("Failed to load vendor punchout list", { variant: 'error' }));

  useEffect(() => {
    if (category && links && orderByCategoryAllowedVendors) {
      setLinks(links => links.map(link => ({ ...link, locked: !orderByCategoryAllowedVendors[link.purchasingVendorId]?.includes(category.id) })))
    }
  }, [links, category]);

  return (
    <div>
      <Button
        variant="text"
        disableElevation
        sx={{ textDecoration: 'underline' }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        color="info"
        endIcon={<Iconify icon="mingcute:down-line" width={16} height={16} />}
      >
        Punchout Order
      </Button>

      <Dialog maxWidth="md" open={confirmPunchout != null} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setConfirmPunchout(null);
      }}>
        <DialogTitle sx={{ pb: 2 }}>Browser has blocked this puchout link</DialogTitle>
        <DialogContent sx={{ typography: 'body2' }}>
          <Box>Your browser has blocked this punchout link to {confirmPunchout?.vendor} from automatically opening.</Box>
          <Box>Please click open link to go to the punchout.</Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { window.open(confirmPunchout?.url); setConfirmPunchout(null); }}>
            Open link
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => setConfirmPunchout(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <StyledMenu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
        <Box>
          {categoryId === '-1' && <Box>
            <Autocomplete
              size="small"
              sx={{
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '& .MuiFormLabelRoot ': 'none',
                m: 1,
              }}
              type="new-password"
              options={categories}
              autoHighlight
              blurOnSelect
              getOptionLabel={(option) => option?.name}
              value={category || null}
              disabled={loadingVendorPunchout !== null}
              onChange={(event, newValue) => setCategory(newValue)}
              renderInput={(params) => <TextField
                {...params}
                size="small"
                label=""
                variant="outlined"
                inputProps={{ ...params.inputProps }}
                autoComplete="off"
              />}
            />
          </Box>}
          {links?.length > 0 ? <>            
            <VendorOrderLinkList
              links={links}
              categoryId={categoryId !== '-1' ? Number(categoryId) : category?.id}
              facilityId={Number(facilityId)}
              loadingVendorPunchout={loadingVendorPunchout}
              setLoadingVendorPunchout={setLoadingVendorPunchout}
              onSuccess={(data, vendorName) => {
                setAnchorEl(null);
                const newWin = window.open(data.successUrl);
                if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') setConfirmPunchout({ url: data.successUrl, vendor: vendorName });
              }}
            />
          </> : <MenuItem>{noVendors ? "User does not have permission for this category" : "Loading..."}</MenuItem>}
        </Box>
      </StyledMenu>
    </div>
  );
}

const StyledMenu = styled((props) => (
  <Menu elevation={0} {...props} />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    // marginTop: theme.spacing(1),
    minWidth: 300,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    backgroundColor: '#fff',
    overflow: 'auto',
    border: 'solid 1px #c1c9d0',
    '& .MuiMenuItem-root': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));
